import { FC, useMemo, useState, type ReactNode } from "react";
import { ChevronRight, ExpandMore } from "@mui/icons-material";
import { Box, Checkbox, Collapse, FormGroup, IconButton, Tooltip, Typography } from "@mui/material";

import type { IComponentFaultFrequencyApi } from "../../interfaces";
import { lineColors } from "../../constants";
import { getFaultFrequencySelectionKey } from "../../utils/line-colors";

function shouldShowFrequencyHz(frequencyHz: number | undefined | null): boolean {
  return typeof frequencyHz === "number" && Number.isFinite(frequencyHz) && frequencyHz > 0;
}

function formatFrequencyHz(frequencyHz: number): string {
  return `${frequencyHz.toFixed(3)} Гц`;
}

function toggleExpandedKey(previous: Set<string>, key: string): Set<string> {
  const next = new Set(previous);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  return next;
}

export interface IComponentFaultFrequencyRow {
  selectionKey: string;
  freqCode: string;
  displayName: string;
  frequencyHz: number;
  coeffToBase: number;
}

interface IFaultFrequencyNodeGroup {
  id: number;
  title: string;
  rows: IComponentFaultFrequencyRow[];
}

interface IFaultFrequencyMechanismGroup {
  key: string;
  title: string;
  nodes: IFaultFrequencyNodeGroup[];
}

function flattenApiItems(items: IComponentFaultFrequencyApi[]): IComponentFaultFrequencyRow[] {
  const out: IComponentFaultFrequencyRow[] = [];
  for (const it of items) {
    if (it.isVisible === false) {
      continue;
    }
    out.push({
      selectionKey: getFaultFrequencySelectionKey(it),
      freqCode: it.freqCode,
      displayName: it.displayName || it.freqCode,
      frequencyHz: it.frequencyHz,
      coeffToBase: it.coeffToBase,
    });
  }
  return out;
}

export interface IFaultFrequenciesPanelProps {
  items: IComponentFaultFrequencyApi[];
  selectedKeys: string[];
  onChangeSelected: (keys: string[]) => void;
  lineColorsByKey: Record<string, string>;
  loading?: boolean;
  rootLabel?: string;
}

export const FaultFrequenciesPanel: FC<IFaultFrequenciesPanelProps> = ({
  items,
  selectedKeys,
  onChangeSelected,
  lineColorsByKey,
  loading = false,
  rootLabel,
}) => {
  const [isRootExpanded, setIsRootExpanded] = useState(true);
  const [expandedMechanisms, setExpandedMechanisms] = useState<Set<string>>(() => new Set());
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => new Set());
  const visible = useMemo(() => {
    return flattenApiItems(items).filter(i => i.selectionKey);
  }, [items]);
  const itemsBySelectionKey = useMemo(
    () => new Map(items.map(item => [getFaultFrequencySelectionKey(item), item])),
    [items],
  );
  const mechanisms = useMemo((): IFaultFrequencyMechanismGroup[] => {
    const nodesById = new Map<number, IFaultFrequencyNodeGroup & { mechanismKey: string; mechanismTitle: string }>();

    for (const row of visible) {
      const item = itemsBySelectionKey.get(row.selectionKey);
      if (item?.idTrainComponent == null) {
        continue;
      }

      const existingNode = nodesById.get(item.idTrainComponent);
      if (existingNode) {
        existingNode.rows.push(row);
        continue;
      }

      const mechanismTitle = item.mechanismName?.trim() || "Узлы вне механизмов";
      const mechanismKey =
        item.idTrainMechanism != null ? `mech-${item.idTrainMechanism}` : `mech-name-${mechanismTitle}`;

      nodesById.set(item.idTrainComponent, {
        id: item.idTrainComponent,
        title: item.trainComponentName || item.componentName,
        rows: [row],
        mechanismKey,
        mechanismTitle,
      });
    }

    const mechanismsByKey = new Map<string, IFaultFrequencyMechanismGroup>();

    for (const node of nodesById.values()) {
      const existingMechanism = mechanismsByKey.get(node.mechanismKey);
      if (existingMechanism) {
        existingMechanism.nodes.push({ id: node.id, title: node.title, rows: node.rows });
      } else {
        mechanismsByKey.set(node.mechanismKey, {
          key: node.mechanismKey,
          title: node.mechanismTitle,
          nodes: [{ id: node.id, title: node.title, rows: node.rows }],
        });
      }
    }

    return [...mechanismsByKey.values()]
      .map(mechanism => ({
        ...mechanism,
        nodes: [...mechanism.nodes].sort((a, b) => a.title.localeCompare(b.title, "ru")),
      }))
      .sort((a, b) => a.title.localeCompare(b.title, "ru"));
  }, [itemsBySelectionKey, visible]);
  const isTree = mechanisms.length > 0;
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const aggregateLabel = rootLabel?.trim() || "Все частоты неисправностей";

  const setRowsSelected = (keys: string[], checked: boolean) => {
    const changedKeys = new Set(keys);
    onChangeSelected(
      checked
        ? [...new Set([...selectedKeys, ...keys])]
        : selectedKeys.filter(selectedKey => !changedKeys.has(selectedKey)),
    );
  };

  const getSelectionState = (keys: string[]) => {
    const selectedCount = keys.reduce((count, key) => count + Number(selectedSet.has(key)), 0);
    return {
      checked: keys.length > 0 && selectedCount === keys.length,
      indeterminate: selectedCount > 0 && selectedCount < keys.length,
    };
  };

  const renderExpandableRow = (
    key: string,
    label: ReactNode,
    isExpanded: boolean,
    onToggle: () => void,
    checkboxProps: {
      checked: boolean;
      indeterminate?: boolean;
      onChange: (checked: boolean) => void;
    },
    options?: { tooltip?: string; indentLevel?: number },
  ) => (
    <Box
      key={key}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        minWidth: 0,
        pl: options?.indentLevel ? options.indentLevel * 1.5 : 0,
      }}
    >
      <IconButton size="small" onClick={onToggle} sx={{ mt: -0.25, flexShrink: 0 }}>
        {isExpanded ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
      </IconButton>
      <Checkbox
        size="small"
        checked={checkboxProps.checked}
        indeterminate={checkboxProps.indeterminate}
        onChange={(_, checked) => checkboxProps.onChange(checked)}
        sx={{ mt: -0.25, flexShrink: 0 }}
      />
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.35, pb: 0.25 }}>
        {options?.tooltip ? (
          <Tooltip title={options.tooltip} placement="left">
            <Typography variant="body2" sx={{ lineHeight: 1.35, wordBreak: "break-word" }}>
              {label}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2" sx={{ lineHeight: 1.35, wordBreak: "break-word" }}>
            {label}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderFrequencyRow = (row: IComponentFaultFrequencyRow, indentLevel = 3) => {
    const checked = selectedSet.has(row.selectionKey);
    const item = itemsBySelectionKey.get(row.selectionKey);
    const showHz = shouldShowFrequencyHz(row.frequencyHz);
    const lineCount = item?.lines?.length ?? 0;
    const linesHint = lineCount > 1 ? `При выборе на графике - ${lineCount} вертикальных линий (весь ряд)` : "";
    const name = row.displayName || row.freqCode;
    const lineColor = lineColorsByKey[row.selectionKey] ?? lineColors[0];
    const tooltip = [linesHint, showHz ? formatFrequencyHz(row.frequencyHz) : ""].filter(Boolean).join(" · ");

    return (
      <Box
        key={row.selectionKey}
        sx={{
          display: "grid",
          gridTemplateColumns: "auto auto minmax(0, 1fr) auto",
          alignItems: "start",
          columnGap: 0.5,
          minWidth: 0,
          py: 0.12,
          pl: indentLevel * 1.5,
        }}
      >
        <Checkbox
          size="small"
          checked={checked}
          onChange={(_, nextChecked) => setRowsSelected([row.selectionKey], nextChecked)}
          sx={{ mt: -0.25 }}
        />
        <Box
          aria-hidden
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: lineColor,
            border: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            mt: 0.85,
          }}
        />
        <Tooltip title={tooltip || name} placement="left">
          <Typography variant="body2" sx={{ minWidth: 0, lineHeight: 1.35, wordBreak: "break-word" }}>
            {name}
          </Typography>
        </Tooltip>
        {showHz ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              flexShrink: 0,
              textAlign: "right",
              fontVariantNumeric: "tabular-nums",
              whiteSpace: "nowrap",
              pt: 0.1,
            }}
          >
            {formatFrequencyHz(row.frequencyHz)}
          </Typography>
        ) : (
          <Box />
        )}
      </Box>
    );
  };

  if (!visible.length && !loading) {
    return null;
  }

  return (
    <Box
      sx={{
        flexShrink: 0,
        alignSelf: "stretch",
        width: 360,
        maxWidth: "min(420px, 45vw)",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        border: t => `1px solid ${t.palette.divider}`,
        borderRadius: 1,
        p: 1.5,
        overflow: "hidden",
        boxSizing: "border-box",
        mt: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, flexShrink: 0 }}>
        Частоты неисправностей
      </Typography>
      {loading ? (
        <Typography variant="body2">Загрузка…</Typography>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto" }}>
          {isTree ? (
            <FormGroup sx={{ gap: 0.25, minWidth: 0 }}>
              {(() => {
                const allKeys = visible.map(row => row.selectionKey);
                const rootState = getSelectionState(allKeys);
                return (
                  <>
                    {renderExpandableRow(
                      "all-fault-frequencies",
                      aggregateLabel,
                      isRootExpanded,
                      () => setIsRootExpanded(value => !value),
                      {
                        checked: rootState.checked,
                        indeterminate: rootState.indeterminate,
                        onChange: checked => setRowsSelected(allKeys, checked),
                      },
                    )}
                    <Collapse in={isRootExpanded} unmountOnExit>
                      {mechanisms.map(mechanism => {
                        const mechanismKeys = mechanism.nodes.flatMap(node => node.rows.map(row => row.selectionKey));
                        const mechanismState = getSelectionState(mechanismKeys);
                        const isMechanismExpanded = expandedMechanisms.has(mechanism.key);

                        return (
                          <Box key={mechanism.key} sx={{ minWidth: 0 }}>
                            {renderExpandableRow(
                              mechanism.key,
                              mechanism.title,
                              isMechanismExpanded,
                              () => setExpandedMechanisms(previous => toggleExpandedKey(previous, mechanism.key)),
                              {
                                checked: mechanismState.checked,
                                indeterminate: mechanismState.indeterminate,
                                onChange: checked => setRowsSelected(mechanismKeys, checked),
                              },
                              { indentLevel: 1 },
                            )}
                            <Collapse in={isMechanismExpanded} unmountOnExit>
                              {mechanism.nodes.map(node => {
                                const nodeKey = `${mechanism.key}-node-${node.id}`;
                                const nodeKeys = node.rows.map(row => row.selectionKey);
                                const nodeState = getSelectionState(nodeKeys);
                                const isNodeExpanded = expandedNodes.has(nodeKey);

                                return (
                                  <Box key={nodeKey} sx={{ minWidth: 0 }}>
                                    {renderExpandableRow(
                                      nodeKey,
                                      node.title,
                                      isNodeExpanded,
                                      () => setExpandedNodes(previous => toggleExpandedKey(previous, nodeKey)),
                                      {
                                        checked: nodeState.checked,
                                        indeterminate: nodeState.indeterminate,
                                        onChange: checked => setRowsSelected(nodeKeys, checked),
                                      },
                                      { tooltip: node.title, indentLevel: 2 },
                                    )}
                                    <Collapse in={isNodeExpanded} unmountOnExit>
                                      <FormGroup sx={{ gap: 0.25, minWidth: 0 }}>
                                        {node.rows.map(row => renderFrequencyRow(row))}
                                      </FormGroup>
                                    </Collapse>
                                  </Box>
                                );
                              })}
                            </Collapse>
                          </Box>
                        );
                      })}
                    </Collapse>
                  </>
                );
              })()}
            </FormGroup>
          ) : (
            <FormGroup sx={{ gap: 0.25, minWidth: 0 }}>{visible.map(row => renderFrequencyRow(row, 0))}</FormGroup>
          )}
        </Box>
      )}
    </Box>
  );
};
